import { useEffect, useState } from 'react'
import {
  Users,
  Gift,
  TrendingUp,
  AlertTriangle,
  Activity,
  DollarSign,
  Calendar,
  ArrowUpRight,
  FileText,
  ClipboardCheck,
  Plus,
} from 'lucide-react'
import { Link } from 'react-router-dom'
import { statisticsApi, pointsApi } from '../../lib/api'
import { useAuth } from '../../lib/auth'
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
} from 'recharts'

const COLORS = ['#2B5D3A', '#4A90E2', '#F5A623', '#E74C3C', '#9B59B6']

interface OverviewData {
  total_users: number
  month_new_users: number
  active_users: number
}

interface TrendData {
  date: string
  points: number
  count: number
}

interface GiftRank {
  name: string
  count: number
}

interface EventPointsStat {
  event_type_name: string
  total_points: number
  record_count: number
}

interface RiskAlert {
  id: string
  description: string
  status: string
  created_at: string
}

interface PointRecord {
  id: string
  user_name?: string
  event_type_name?: string
  points: number
  created_at: string
  status: string
}

const Dashboard = () => {
  const { user } = useAuth()
  const isSupervisor = user?.role === 'medical' && user?.medicalRole === 'supervisor'
  const hospitalId = user?.hospitalId ?? undefined
  const [overview, setOverview] = useState<OverviewData | null>(null)
  const [pointsOverview, setPointsOverview] = useState<any>(null)
  const [trends, setTrends] = useState<TrendData[]>([])
  const [giftRanking, setGiftRanking] = useState<GiftRank[]>([])
  const [riskAlerts, setRiskAlerts] = useState<RiskAlert[]>([])
  const [recentPoints, setRecentPoints] = useState<PointRecord[]>([])
  const [pendingRecords, setPendingRecords] = useState<PointRecord[]>([])
  const [eventPointsStats, setEventPointsStats] = useState<EventPointsStat[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const load = async () => {
      setLoading(true)
      try {
        const [overviewRes, pointsOverviewRes, trendsRes, giftsRes, pointsRes, pendingRes, eventStatsRes] = await Promise.allSettled([
          isSupervisor ? statisticsApi.usersOverview({ hospital_id: hospitalId }) : statisticsApi.usersOverview(),
          isSupervisor ? statisticsApi.pointsOverview({ hospital_id: hospitalId }) : statisticsApi.pointsOverview(),
          isSupervisor ? statisticsApi.pointsDaily({ days: 7, hospital_id: hospitalId }) : statisticsApi.pointsDaily({ days: 7 }),
          isSupervisor ? statisticsApi.popularGifts({ hospital_id: hospitalId }) : statisticsApi.popularGifts(),
          isSupervisor ? pointsApi.records({ page: 1, page_size: 5, hospital_id: hospitalId }) : pointsApi.records({ page: 1, page_size: 5 }),
          isSupervisor ? pointsApi.records({ page: 1, page_size: 5, status: 'pending', hospital_id: hospitalId }) : Promise.resolve(null),
          isSupervisor ? statisticsApi.pointsByEvent({ days: 30, hospital_id: hospitalId }) : statisticsApi.pointsByEvent({ days: 30 }),
        ])
        // 机构负责人不调用风控API，直接置空
        setRiskAlerts([])

        if (overviewRes.status === 'fulfilled') setOverview(overviewRes.value as OverviewData)
        if (pointsOverviewRes.status === 'fulfilled') setPointsOverview(pointsOverviewRes.value)
        if (trendsRes.status === 'fulfilled') {
          const d = trendsRes.value as { items?: TrendData[] } | TrendData[]
          setTrends(Array.isArray(d) ? d : (d as { items?: TrendData[] }).items ?? [])
        }
        if (giftsRes.status === 'fulfilled') {
          const d = giftsRes.value as { items?: any[] } | any[]
          // popular-gifts 返回 {items: [{gift_name, exchange_count, ...}]} 需转换为 {name, count}
          const list = Array.isArray(d) ? d : (d as { items?: any[] }).items ?? []
          const mapped = list.slice(0, 5).map((g: any) => ({
            name: g.gift_name || g.name || '未知礼品',
            count: g.exchange_count || g.count || 0
          }))
          setGiftRanking(mapped)
        }
        if (pointsRes.status === 'fulfilled') {
          const d = pointsRes.value as { items?: PointRecord[]; records?: PointRecord[] } | PointRecord[]
          // points API 返回 {total, records} 格式
          const recordList = Array.isArray(d) ? d : ((d as { items?: PointRecord[] }).items || (d as { records?: PointRecord[] }).records || [])
          setRecentPoints(recordList)
        }
        if (eventStatsRes.status === 'fulfilled') {
          const d = eventStatsRes.value as { items?: EventPointsStat[] } | EventPointsStat[]
          const list = Array.isArray(d) ? d : (d as { items?: EventPointsStat[] }).items ?? []
          setEventPointsStats(list.slice(0, 6))  // 最多显示6个事件类型
        }
        if (pendingRes.status === 'fulfilled' && pendingRes.value) {
          const d = pendingRes.value as { items?: PointRecord[]; records?: PointRecord[] } | PointRecord[]
          const list = Array.isArray(d) ? d : ((d as { items?: PointRecord[] }).items || (d as { records?: PointRecord[] }).records || [])
          setPendingRecords(list.slice(0, 5))
        }
      } catch (e) {
        console.error('Dashboard load error:', e)
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [])

  const statCards = [
    {
      title: '总用户数',
      value: overview?.total_users?.toLocaleString() ?? '-',
      icon: Users,
      color: 'from-green-500 to-green-600',
      change: '注册用户',
      trend: 'up',
    },
    {
      title: '本月新增',
      value: overview?.month_new_users?.toLocaleString() ?? '-',
      icon: TrendingUp,
      color: 'from-blue-500 to-blue-600',
      change: '本月新注册',
      trend: 'up',
    },
    {
      title: '活跃用户',
      value: overview?.active_users?.toLocaleString() ?? '-',
      icon: Gift,
      color: 'from-orange-500 to-orange-600',
      change: '活跃用户',
      trend: 'up',
    },
    {
      title: isSupervisor ? '待审核事件' : '待处理告警',
      value: isSupervisor
        ? (pointsOverview?.pending_count ?? riskAlerts.length).toString()
        : riskAlerts.length.toString(),
      icon: isSupervisor ? ClipboardCheck : AlertTriangle,
      color: 'from-red-500 to-red-600',
      change: isSupervisor ? '需要审核' : '需要处理',
      trend: 'up',
    },
  ]

  const quickActions = isSupervisor
    ? [
        { to: '/admin/users', icon: Users, label: '会员管理', color: 'text-green-600' },
        { to: '/admin/medical-staff', icon: Activity, label: '医护管理', color: 'text-blue-600' },
        { to: '/admin/events', icon: Calendar, label: '事件类型', color: 'text-orange-600' },
        { to: '/admin/gifts', icon: Gift, label: '礼品管理', color: 'text-purple-600' },
        { to: '/admin/point-entry', icon: Plus, label: '积分录入', color: 'text-teal-600' },
        { to: '/admin/verify', icon: ClipboardCheck, label: '事件审核', color: 'text-red-600' },
      ]
    : [
        { to: '/admin/users', icon: Users, label: '会员管理', color: 'text-green-600' },
        { to: '/admin/medical-staff', icon: Activity, label: '医护管理', color: 'text-blue-600' },
        { to: '/admin/events', icon: Calendar, label: '事件类型', color: 'text-orange-600' },
        { to: '/admin/gifts', icon: Gift, label: '礼品管理', color: 'text-purple-600' },
        { to: '/admin/statistics', icon: DollarSign, label: '数据统计', color: 'text-teal-600' },
        { to: '/admin/audit-log', icon: TrendingUp, label: '操作日志', color: 'text-red-600' },
      ]

  return (
    <div className="space-y-6">
      {/* 快捷操作 */}
      <div className="bg-white rounded-2xl shadow-md p-6">
        <h3 className="text-lg font-bold text-gray-800 mb-4">快捷操作</h3>
        <div className="grid grid-cols-3 md:grid-cols-6 gap-4">
          {quickActions.map(({ to, icon: Icon, label, color }) => (
            <Link
              key={to}
              to={to}
              className="flex flex-col items-center p-4 bg-gray-50 hover:bg-green-50 rounded-xl transition-colors"
            >
              <Icon className={`w-8 h-8 ${color} mb-2`} />
              <span className="text-sm font-medium text-gray-700">{label}</span>
            </Link>
          ))}
        </div>
      </div>

      {/* 统计卡片 */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((card, index) => {
          const Icon = card.icon
          return (
            <div key={index} className="bg-white rounded-2xl shadow-md overflow-hidden hover:shadow-lg transition-shadow">
              <div className="p-6">
                <div className="flex items-center justify-between">
                  <div className={`p-3 rounded-xl bg-gradient-to-r ${card.color}`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex items-center text-sm font-medium text-green-600">
                    <ArrowUpRight className="w-4 h-4" />
                    {card.change}
                  </div>
                </div>
                <div className="mt-4">
                  <p className="text-3xl font-bold text-gray-800">
                    {loading ? <span className="inline-block w-16 h-8 bg-gray-200 rounded animate-pulse" /> : card.value}
                  </p>
                  <p className="text-gray-500 text-sm mt-1">{card.title}</p>
                </div>
              </div>
            </div>
          )
        })}
      </div>

      {/* 图表区域 */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* 积分趋势图 */}
        <div className="lg:col-span-2 bg-white rounded-2xl shadow-md p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-bold text-gray-800">近7日积分趋势</h3>
            <div className="flex items-center space-x-4 text-sm">
              <div className="flex items-center">
                <div className="w-3 h-3 rounded-full bg-green-600 mr-2" />
                <span className="text-gray-600">积分</span>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 rounded-full bg-blue-500 mr-2" />
                <span className="text-gray-600">记录数</span>
              </div>
            </div>
          </div>
          <div className="h-72">
            {trends.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={trends}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="date" stroke="#9CA3AF" fontSize={12} />
                  <YAxis stroke="#9CA3AF" fontSize={12} />
                  <Tooltip contentStyle={{ backgroundColor: '#fff', border: 'none', borderRadius: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} />
                  <Line type="monotone" dataKey="points" stroke="#2B5D3A" strokeWidth={3} dot={{ fill: '#2B5D3A', r: 4 }} activeDot={{ r: 6 }} />
                  <Line type="monotone" dataKey="count" stroke="#4A90E2" strokeWidth={3} dot={{ fill: '#4A90E2', r: 4 }} activeDot={{ r: 6 }} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-gray-400">
                {loading ? '加载中...' : '暂无数据'}
              </div>
            )}
          </div>
        </div>

        {/* 礼品兑换排行 */}
        <div className="bg-white rounded-2xl shadow-md p-6">
          <h3 className="text-lg font-bold text-gray-800 mb-6">礼品兑换排行</h3>
          <div className="h-56">
            {giftRanking.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={giftRanking} cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={3} dataKey="count" nameKey="name">
                    {giftRanking.map((_, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={{ backgroundColor: '#fff', border: 'none', borderRadius: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-gray-400 text-sm">
                {loading ? '加载中...' : '暂无兑换数据'}
              </div>
            )}
          </div>
          <div className="mt-4 space-y-2">
            {giftRanking.slice(0, 4).map((gift, index) => (
              <div key={gift.name} className="flex items-center justify-between text-sm">
                <div className="flex items-center">
                  <div className="w-3 h-3 rounded-full mr-2" style={{ backgroundColor: COLORS[index] }} />
                  <span className="text-gray-600">{gift.name}</span>
                </div>
                <span className="font-medium text-gray-800">{gift.count}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* 事件类型积分统计 */}
      {eventPointsStats.length > 0 && (
        <div className="bg-white rounded-2xl shadow-md p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-bold text-gray-800">各事件类型积分统计</h3>
            <Link to="/admin/events" className="text-green-600 hover:text-green-700 text-sm font-medium">事件类型管理</Link>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={eventPointsStats} layout="vertical" margin={{ left: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
                <XAxis type="number" stroke="#9CA3AF" fontSize={12} />
                <YAxis dataKey="event_type_name" type="category" width={100} stroke="#9CA3AF" fontSize={12}
                  tickFormatter={(v) => v.length > 6 ? v.slice(0, 6) + '...' : v} />
                <Tooltip contentStyle={{ backgroundColor: '#fff', border: 'none', borderRadius: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                  formatter={(value: number, name: string) => [value, name === 'total_points' ? '积分' : '记录数']} />
                <Bar dataKey="total_points" fill="#2B5D3A" radius={[0, 4, 4, 0]} name="积分" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* 底部信息区域 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* 最新积分记录 */}
        <div className="bg-white rounded-2xl shadow-md p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-gray-800">最新积分记录</h3>
            <Link to="/admin/point-records" className="text-green-600 hover:text-green-700 text-sm font-medium">查看全部</Link>
          </div>
          <div className="space-y-4">
            {recentPoints.length > 0 ? recentPoints.slice(0, 5).map((record) => (
              <div key={record.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                    <Activity className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-800">{record.user_name ?? '用户'}</p>
                    <p className="text-sm text-gray-500">{record.event_type_name ?? '积分事件'}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-bold text-green-600">+{record.points}</p>
                  <p className="text-xs text-gray-500">{record.created_at?.slice(0, 10)}</p>
                </div>
              </div>
            )) : (
              <p className="text-gray-400 text-center py-4">{loading ? '加载中...' : '暂无积分记录'}</p>
            )}
          </div>
        </div>

        {/* 待审核事件 / 风控告警 */}
        <div className="bg-white rounded-2xl shadow-md p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-gray-800">{isSupervisor ? '待审核事件' : '风控告警'}</h3>
            <Link to={isSupervisor ? '/admin/verify' : '/admin/risk-control'} className="text-green-600 hover:text-green-700 text-sm font-medium">查看全部</Link>
          </div>
          <div className="space-y-4">
            {isSupervisor ? (
              pendingRecords.length > 0 ? pendingRecords.map((record) => (
                <div key={record.id} className="p-4 rounded-xl bg-orange-50">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-3">
                      <ClipboardCheck className="w-5 h-5 mt-0.5 text-orange-500" />
                      <div>
                        <p className="font-medium text-gray-800">{record.user_name ?? '用户'}</p>
                        <p className="text-sm text-gray-500">{record.event_type_name ?? '积分事件'}</p>
                        <p className="text-xs text-gray-400 mt-1">+{record.points}分 · {record.created_at?.slice(0, 10)}</p>
                      </div>
                    </div>
                    <span className="px-2 py-1 text-xs font-medium rounded-full bg-orange-100 text-orange-600">待审核</span>
                  </div>
                </div>
              )) : (
                <div className="flex flex-col items-center py-8 text-gray-400">
                  <FileText className="w-8 h-8 mb-2 opacity-30" />
                  <p className="text-sm">{loading ? '加载中...' : '暂无待审核事件'}</p>
                </div>
              )
            ) : (
              riskAlerts.length > 0 ? riskAlerts.map((alert) => (
                <div key={alert.id} className={`p-4 rounded-xl ${alert.status === 'pending' ? 'bg-red-50' : 'bg-gray-50'}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-3">
                      <AlertTriangle className={`w-5 h-5 mt-0.5 ${alert.status === 'pending' ? 'text-red-500' : 'text-gray-400'}`} />
                      <div>
                        <p className="text-sm text-gray-600 mt-1">{alert.description}</p>
                        <p className="text-xs text-gray-400 mt-2">{alert.created_at?.slice(0, 16)}</p>
                      </div>
                    </div>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                      alert.status === 'pending' ? 'bg-red-100 text-red-600' : 'bg-green-100 text-green-600'
                    }`}>
                      {alert.status === 'pending' ? '待处理' : '已处理'}
                    </span>
                  </div>
                </div>
              )) : (
                <div className="flex flex-col items-center py-8 text-gray-400">
                  <FileText className="w-8 h-8 mb-2 opacity-30" />
                  <p className="text-sm">{loading ? '加载中...' : '暂无风控告警'}</p>
                </div>
              )
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default Dashboard
